import { ROUTES } from 'constants/routes';
import { AVAILABLE_SCHOOL_IDS } from 'constants/schoolsOfThoughtCatalog';
import type { GlobalSearchFilter, SearchableSchoolSlug } from 'shared/search/types';

export type SchoolSearchMeta = {
  slug: SearchableSchoolSlug;
  labelEn: string;
  labelHi: string;
  shortLabelEn: string;
  shortLabelHi: string;
  learnRoute: string;
  keywords: string;
};

export const SCHOOL_SEARCH_META: Record<SearchableSchoolSlug, SchoolSearchMeta> = {
  parashari: {
    slug: 'parashari',
    labelEn: 'Parashari Jyotish',
    labelHi: 'पाराशरी ज्योतिष',
    shortLabelEn: 'Parashari',
    shortLabelHi: 'पाराशरी',
    learnRoute: ROUTES.PARASHARI_LEARN,
    keywords: 'parashari jyotish bphs vedic classical graha bhava dasha',
  },
  jaimini: {
    slug: 'jaimini',
    labelEn: 'Jaimini Astrology',
    labelHi: 'जैमिनी ज्योतिष',
    shortLabelEn: 'Jaimini',
    shortLabelHi: 'जैमिनी',
    learnRoute: ROUTES.JAIMINI_LEARN,
    keywords: 'jaimini chara karaka arudha rashi drishti dasha sutra',
  },
  kp: {
    slug: 'kp',
    labelEn: 'KP System',
    labelHi: 'KP पद्धति',
    shortLabelEn: 'KP',
    shortLabelHi: 'KP',
    learnRoute: ROUTES.KP_LEARN,
    keywords: 'kp krishnamurti paddhati sub lord cusp significator stellar',
  },
  bnn: {
    slug: 'bnn',
    labelEn: 'Bhrigu Nandi Nadi',
    labelHi: 'भृगु नंदी नाड़ी',
    shortLabelEn: 'BNN',
    shortLabelHi: 'BNN',
    learnRoute: ROUTES.BNN_LEARN,
    keywords: 'bnn bhrigu nandi nadi nadi astrology sambandha karakatwa transit',
  },
  'lal-kitab': {
    slug: 'lal-kitab',
    labelEn: 'Lal Kitab',
    labelHi: 'लाल किताब',
    shortLabelEn: 'Lal Kitab',
    shortLabelHi: 'लाल किताब',
    learnRoute: ROUTES.LAL_KITAB_LEARN,
    keywords: 'lal kitab remedies debts kundli totke upay',
  },
  numerology: {
    slug: 'numerology',
    labelEn: 'Chaldean Numerology',
    labelHi: 'चाल्डियन / शॉल्डियन अंक ज्योतिष',
    shortLabelEn: 'Numerology',
    shortLabelHi: 'अंक ज्योतिष',
    learnRoute: ROUTES.NUMEROLOGY_LEARN,
    keywords: 'chaldean numerology ankotish mulank bhagyank compound name analysis',
  },
  'surya-siddhanta': {
    slug: 'surya-siddhanta',
    labelEn: 'Surya Siddhanta',
    labelHi: 'सूर्य सिद्धान्त',
    shortLabelEn: 'Surya Siddhanta',
    shortLabelHi: 'सूर्य सिद्धान्त',
    learnRoute: ROUTES.SURYA_SIDDHANTA_LEARN,
    keywords: 'surya siddhanta mahayuga ganita ayanamsha lahiri nakshatra panchang planetary periods',
  },
  panchang: {
    slug: 'panchang',
    labelEn: 'Panchang',
    labelHi: 'पंचांग',
    shortLabelEn: 'Panchang',
    shortLabelHi: 'पंचांग',
    learnRoute: ROUTES.PANCHANG_LEARN,
    keywords: 'panchang tithi vara nakshatra yoga karana muhurta rahu kaal tara bala ekadashi festival',
  },
  vastu: {
    slug: 'vastu',
    labelEn: 'Vastu Shastra',
    labelHi: 'वास्तु शास्त्र',
    shortLabelEn: 'Vastu',
    shortLabelHi: 'वास्तु',
    learnRoute: ROUTES.VASTU_LEARN,
    keywords: 'vastu shastra purusha mandala brahmasthan pada direction kitchen bedroom muhurta plot',
  },
  'swara-vigyan': {
    slug: 'swara-vigyan',
    labelEn: 'Swara Vigyan',
    labelHi: 'स्वर विज्ञान',
    shortLabelEn: 'Swara Vigyan',
    shortLabelHi: 'स्वर विज्ञान',
    learnRoute: ROUTES.SWARA_VIGYAN_LEARN,
    keywords: 'swara vigyan breath nostril ida pingala sushumna tithi sunrise panchang pranayama',
  },
  'yantra-vidya': {
    slug: 'yantra-vidya',
    labelEn: 'Yantra Vidya',
    labelHi: 'यंत्र विद्या',
    shortLabelEn: 'Yantra Vidya',
    shortLabelHi: 'यंत्र विद्या',
    learnRoute: ROUTES.YANTRA_VIDYA_LEARN,
    keywords: 'yantra vidya sacred geometry bindu shatkona shri chakra lotus bhupura mandala tantra',
  },
  tantra: {
    slug: 'tantra',
    labelEn: 'Tantra',
    labelHi: 'तंत्र',
    shortLabelEn: 'Tantra',
    shortLabelHi: 'तंत्र',
    learnRoute: ROUTES.TANTRA_LEARN,
    keywords: 'tantra shiva shakti mantra yantra mudra nyasa kundalini chakra agama diksha guru sadhana',
  },
};

/** Active searchable schools in display order. */
export const SEARCHABLE_SCHOOL_SLUGS: SearchableSchoolSlug[] = AVAILABLE_SCHOOL_IDS.filter(
  (id): id is SearchableSchoolSlug => id in SCHOOL_SEARCH_META,
);

export function getSchoolSearchMeta(slug: SearchableSchoolSlug): SchoolSearchMeta {
  return SCHOOL_SEARCH_META[slug];
}

export function pickSchoolSearchLabel(
  slug: SearchableSchoolSlug,
  showEnglish: boolean,
  short = false,
): string {
  const meta = SCHOOL_SEARCH_META[slug];
  if (short) return showEnglish ? meta.shortLabelEn : meta.shortLabelHi;
  return showEnglish ? meta.labelEn : meta.labelHi;
}

export function isSchoolFilterActive(
  filter: GlobalSearchFilter,
  schoolSlug: SearchableSchoolSlug,
): boolean {
  return filter === 'all' || filter === schoolSlug;
}
